import { useMutation, useQueryClient } from '@tanstack/react-query';
import fetchClient from '@eureca/admin-api-sdk/client';
import type {
  PatchAssessmentsId200,
  PatchAssessmentsIdMutationRequest,
} from '@eureca/admin-api-sdk/types';
import { ASSESSMENTS_LIST_KEY } from './assessments-query';

export class AssessmentMutationError extends Error {
  readonly name = 'AssessmentMutationError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/**
 * PATCH /assessments/:id — usado pela tela "Testes" pra editar a política de
 * reuso cross-vaga de um PRESET (`{inheritable, inheritTtlMonths}` só; o Core
 * responde 409 `preset_immutable` pra qualquer outro campo em preset, 422
 * `adhoc_not_inheritable` se a política vier pra um ad_hoc, e 403 se o token
 * não tiver a role `admin`). O id vem por chamada (alvo é por-linha).
 */
export function usePatchAssessment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      patch: PatchAssessmentsIdMutationRequest;
    }): Promise<PatchAssessmentsId200> => {
      const res = await fetchClient<PatchAssessmentsId200>({
        method: 'PATCH',
        url: `/assessments/${input.id}`,
        data: input.patch,
      });
      if (res.error) {
        throw new AssessmentMutationError(
          `Failed to update assessment (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ASSESSMENTS_LIST_KEY });
    },
  });
}
